import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { X, Download } from "lucide-react";
import { getCookieConsent } from "@/components/marketplace/CookieConsent";

const DISMISS_KEY = "getjeweldesigns_pwa_dismissed_at";
const DISMISS_TTL_MS = 1000 * 60 * 60 * 24 * 7; // 7 days

function isStandalone() {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isIOS() {
  const ua = window.navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}

// Signed-in areas are dense working screens — the card is pinned over the
// bottom-right, which is exactly where message composers put their Send button.
// Offer the install on the marketing pages instead, where it obstructs nothing.
const APP_ROUTES = ["/admin", "/seller", "/freelancer"];

export default function InstallPrompt() {
  const { pathname } = useLocation();
  const inAppArea = APP_ROUTES.some((p) => pathname === p || pathname.startsWith(p + "/"));
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIOS, setShowIOS] = useState(false);
  // Hold the install prompt back until the cookie banner has been dealt with,
  // so the two bottom-pinned prompts never stack on top of each other.
  const [consentDecided, setConsentDecided] = useState(() => {
    const c = getCookieConsent();
    return c === "accepted" || c === "declined";
  });

  // Dismissing the cookie banner used to hand the same screen space straight to
  // this prompt, which on a phone covered the hero's primary call to action.
  // Wait for a sign of engagement — scrolling past the hero, or dwelling a
  // while — so it arrives after the visitor has seen what the page offers.
  const [engaged, setEngaged] = useState(false);

  useEffect(() => {
    if (consentDecided) return;
    const onDecided = () => setConsentDecided(true);
    window.addEventListener("getjeweldesigns:cookie-decided", onDecided);
    return () => window.removeEventListener("getjeweldesigns:cookie-decided", onDecided);
  }, [consentDecided]);

  useEffect(() => {
    if (engaged) return;
    const mark = () => setEngaged(true);
    const onScroll = () => { if (window.scrollY > window.innerHeight * 0.75) mark(); };
    window.addEventListener("scroll", onScroll, { passive: true });
    const t = setTimeout(mark, 25000);
    onScroll(); // deep-linked or restored scroll position already counts
    return () => { window.removeEventListener("scroll", onScroll); clearTimeout(t); };
  }, [engaged]);

  useEffect(() => {
    if (isStandalone()) return;
    const dismissed = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (dismissed && Date.now() - dismissed < DISMISS_TTL_MS) return;

    // Android / Chrome: capture the beforeinstallprompt event
    const onBIP = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onBIP);

    // iOS Safari has no prompt event — show a manual "Add to Home Screen" hint
    if (isIOS()) {
      // Delay slightly so it doesn't jump on first paint
      const t = setTimeout(() => setShowIOS(true), 4000);
      return () => {
        clearTimeout(t);
        window.removeEventListener("beforeinstallprompt", onBIP);
      };
    }
    return () => window.removeEventListener("beforeinstallprompt", onBIP);
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
    setShowIOS(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try {
      const choice = await deferred.userChoice;
      if (choice?.outcome === "accepted") {
        setVisible(false);
      } else {
        dismiss();
      }
    } catch {}
    setDeferred(null);
  };

  if (inAppArea || (!visible && !showIOS) || !consentDecided || !engaged) return null;

  return (
    <div
      data-testid="pwa-install-banner"
      role="dialog"
      aria-labelledby="pwa-install-title"
      className="fixed left-4 right-4 md:left-auto md:right-6 md:w-[380px] z-50 border hairline bg-card/95 backdrop-blur-md p-4 md:p-5 shadow-xl fade-in"
      style={{ bottom: "calc(1.5rem + env(safe-area-inset-bottom))" }}
    >
      <button
        aria-label="Dismiss install prompt"
        data-testid="pwa-dismiss"
        onClick={dismiss}
        className="absolute top-1 right-1 p-3 text-muted-foreground hover:text-foreground transition-colors duration-300"
      >
        <X className="h-4 w-4" strokeWidth={1} />
      </button>
      <div className="text-xs uppercase tracking-[0.3em] text-primary mb-2 md:mb-3 pr-8">Install GetJewelDesigns</div>
      {visible && !showIOS && (
        <>
          <div id="pwa-install-title" className="font-serif text-xl md:text-2xl mb-1 md:mb-2 leading-tight pr-8">Add to your home screen.</div>
          {/* The full pitch is desktop-only; on a phone this sits over content. */}
          <p className="hidden md:block text-sm text-muted-foreground mb-4">Faster access, an app-like experience, and offline support for the pages you've visited.</p>
          <button
            data-testid="pwa-install-btn"
            onClick={install}
            className="mt-3 md:mt-0 w-full inline-flex items-center justify-center gap-2 border border-primary text-primary px-5 py-3 text-xs uppercase tracking-widest hover:bg-primary hover:text-primary-foreground transition-colors duration-300"
          >
            <Download className="h-4 w-4" strokeWidth={1} /> Install app
          </button>
        </>
      )}
      {showIOS && (
        <>
          <div id="pwa-install-title" className="font-serif text-xl md:text-2xl mb-2 leading-tight pr-8">Install on iPhone.</div>
          <ol className="text-sm text-muted-foreground space-y-1 md:space-y-2 mb-2">
            <li>1. Tap the <span className="text-primary">Share</span> icon in Safari.</li>
            <li>2. Scroll and select <span className="text-primary">Add to Home Screen</span>.</li>
            <li>3. Tap <span className="text-primary">Add</span>.</li>
          </ol>
        </>
      )}
    </div>
  );
}
